import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import CompanyHeader from '../components/CompanyHeader';
import CompanySidebar from '../components/CompanySidebar';
import AISummary from '../components/AISummary';
import ReviewFilters from '../components/ReviewFilters';
import ReviewCard from '../components/ReviewCard';
import RelatedCompanies from '../components/RelatedCompanies';
import TrustExperience from '../components/TrustExperience';
import Footer from '../components/Footer';
import SchemaOrgReviews from '../components/SchemaOrgReviews';
import ReviewSubmissionModal from '../components/ReviewSubmissionModal';
import { getCompanyByDomain, getReviews, getCompanies } from '../services/api';
import { aiSummary } from '../data/mock';

const ReviewPage = () => {
  const { domain } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [relatedCompanies, setRelatedCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('recent');
  const [visibleReviews, setVisibleReviews] = useState(10);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  useEffect(() => {
    if (domain) {
      loadData(domain);
    }
  }, [domain]);

  const loadData = async (companyDomain) => {
    try {
      setLoading(true);
      setError(null);
      
      // Load company data
      const companyData = await getCompanyByDomain(companyDomain);
      setCompany(companyData);
      
      // Load reviews for this company
      const reviewsData = await getReviews(companyData.id);
      setReviews(reviewsData);
      
      // Load related companies (excluding current one)
      const allCompanies = await getCompanies();
      const related = allCompanies
        .filter(c => c.domain !== companyDomain)
        .slice(0, 6);
      setRelatedCompanies(related);
      
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Entreprise non trouvée');
    } finally {
      setLoading(false);
    }
  };

  const sortedReviews = [...reviews].sort((a, b) => {
    if (filter === 'highest') return b.rating - a.rating;
    if (filter === 'lowest') return a.rating - b.rating;
    return new Date(b.created_at) - new Date(a.created_at); // recent
  });

  const displayedReviews = sortedReviews.slice(0, visibleReviews);

  const handleLoadMore = () => {
    setVisibleReviews((prev) => Math.min(prev + 10, reviews.length));
  };

  const handleRelatedCompanyClick = (companyDomain) => {
    navigate(`/review/${companyDomain}`);
    setVisibleReviews(10);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00b67a] mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des données...</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <p className="text-red-600 mb-4">{error || 'Entreprise non trouvée'}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-[#00b67a] text-white rounded hover:bg-[#00a36c] transition-colors"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Données structurées Schema.org pour Google SERP */}
      <SchemaOrgReviews company={company} reviews={reviews} />
      
      {/* Modal de soumission d'avis */}
      <ReviewSubmissionModal 
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        company={company}
      />
      
      <Header />
      <CompanyHeader 
        company={company} 
        onWriteReview={() => setIsReviewModalOpen(true)}
      />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* AI Summary */}
            <AISummary summary={aiSummary} />
            
            {/* Filters */}
            <ReviewFilters
              selectedFilter={filter}
              onFilterChange={setFilter}
            />
            
            {/* Reviews List */}
            <div className="bg-white rounded-lg border">
              {displayedReviews.length > 0 ? (
                displayedReviews.map((review) => (
                  <div key={review.id} className="px-6">
                    <ReviewCard review={review} />
                  </div>
                ))
              ) : (
                <div className="p-6 text-center text-gray-500">
                  Aucun avis pour le moment
                </div>
              )}
              
              {/* Load More Button */}
              {visibleReviews < reviews.length && (
                <div className="p-6 border-t">
                  <button
                    onClick={handleLoadMore}
                    className="w-full py-3 text-[#1a1a1a] font-medium border rounded hover:bg-gray-50 transition-colors"
                  >
                    Voir plus d'avis ({reviews.length - visibleReviews} restants)
                  </button>
                </div>
              )}
            </div>
            
            {/* Related Companies */}
            <RelatedCompanies 
              companies={relatedCompanies} 
              onCompanyClick={handleRelatedCompanyClick}
            />
          </div>
          
          {/* Sidebar */}
          <div className="lg:w-80 flex-shrink-0">
            <div className="sticky top-24">
              <CompanySidebar company={company} />
            </div>
          </div>
        </div>
      </main>
      
      {/* Trust Experience Section */}
      <TrustExperience />
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default ReviewPage;
